import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, of, exhaustMap, concatMap, mergeMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import { QuestionsService } from '../services/questions.service';
import { QuestionDetailService } from '../services/question-detail.service';
import { QuestionCompaniesService } from '../services/question-companies.service';
import { QuestionActions } from './question.actions';
import { selectCategories, selectSearch } from './question.selectors';

@Injectable()
export class QuestionEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _questionsService = inject(QuestionsService);
  private readonly _questionDetailService = inject(QuestionDetailService);
  private readonly _companiesService = inject(QuestionCompaniesService);
  private readonly _store = inject(Store);
  private readonly _toastService = inject(ToastService);

  public readonly loadQuestions$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.loadQuestions, QuestionActions.setSearch),
      concatLatestFrom(() => this._store.select(selectSearch)),
      switchMap(([, search]) =>
        this._questionsService.getQuestions(search, '').pipe(
          map((questions) =>
            QuestionActions.loadQuestionsSuccess({ questions }),
          ),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.loadQuestionsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading questions. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly selectQuestion$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.selectQuestion),
      switchMap(({ id }) =>
        this._questionDetailService.getById(id).pipe(
          map((detail) => QuestionActions.loadDetailSuccess({ detail })),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.loadDetailFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading the question. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly createQuestion$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.createQuestion),
      concatLatestFrom(() => this._store.select(selectCategories)),
      exhaustMap(([{ model }, categories]) =>
        this._questionDetailService.create(model, categories).pipe(
          tap(() => this._toastService.success('Question added successfully')),
          map((detail) =>
            QuestionActions.createQuestionSuccess({ id: detail.id }),
          ),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.createQuestionFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    );
  });

  public readonly createQuestionSuccess$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.createQuestionSuccess),
      mergeMap(({ id }) => [
        QuestionActions.loadQuestions(),
        QuestionActions.selectQuestion({ id }),
      ]),
    );
  });

  public readonly updateQuestion$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.updateQuestion),
      concatLatestFrom(() => this._store.select(selectCategories)),
      concatMap(([{ id, model }, categories]) =>
        this._questionDetailService.update(id, model, categories).pipe(
          tap(() =>
            this._toastService.success('Question updated successfully'),
          ),
          map(() => QuestionActions.updateQuestionSuccess({ id, model })),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.updateQuestionFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    );
  });

  public readonly deleteQuestion$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.deleteQuestion),
      exhaustMap(({ id }) =>
        this._questionDetailService.delete(id).pipe(
          tap(() =>
            this._toastService.success('Question deleted successfully'),
          ),
          map(() => QuestionActions.deleteQuestionSuccess()),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.deleteQuestionFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while deleting the question. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly deleteQuestionSuccess$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.deleteQuestionSuccess),
      map(() => QuestionActions.loadQuestions()),
    );
  });

  public readonly deleteLocalization$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.deleteLocalization),
      concatMap(({ questionId, locale }) =>
        this._questionDetailService.delete(questionId, locale).pipe(
          tap(() =>
            this._toastService.success('Localization deleted successfully'),
          ),
          map(() => QuestionActions.deleteLocalizationSuccess({ locale })),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.deleteLocalizationFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while deleting the localization. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly loadCompanies$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.loadCompanies),
      switchMap(() =>
        this._companiesService.companies().pipe(
          map((companies) =>
            QuestionActions.loadCompaniesSuccess({ companies }),
          ),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.loadCompaniesFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading companies. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly loadCategories$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.loadCategories),
      switchMap(() =>
        this._questionDetailService.categories().pipe(
          map((categories) =>
            QuestionActions.loadCategoriesSuccess({ categories }),
          ),
          this.psCatchError(
            (error) =>
              of(
                QuestionActions.loadCategoriesFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading categories. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly updateQuestionSuccess$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(QuestionActions.updateQuestionSuccess),
      mergeMap(({ id }) => [
        QuestionActions.loadQuestions(),
        QuestionActions.selectQuestion({ id }),
      ]),
    );
  });
}
